import React from "react";
import { Box, Typography, useTheme, Card } from "@mui/material";
import { motion } from "framer-motion";
import PropTypes from "prop-types";

/**
 * HeroSection Component
 *
 * Reusable hero that displays image/video media with optional logo and overlay text.
 */
const HeroSection = ({
  mediaType = "image",
  mediaSrc,
  mediaSrcSet,
  mediaSizes,
  mediaAlt = "Hero image",
  mediaLoading = "eager",
  logoSrc,
  logoHeight = "180px",
  logoTop,
  logoLeft,
  title,
  subtitle,
  description,
  showLogo = false,
  darkOverlay = false,
  overlayOpacity = 0.15,
  textPosition = "bottom-left",
  sx = {},
  ...props
}) => {
  const theme = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8, staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const resolvedLogoTop = logoTop !== undefined ? logoTop : { xs: theme.spacing(3), md: theme.spacing(4) };
  const resolvedLogoLeft = logoLeft !== undefined ? logoLeft : { xs: theme.spacing(3), md: theme.spacing(4) };
  const resolvedLogoHeight = typeof logoHeight === "object" ? logoHeight : { xs: logoHeight, md: logoHeight };

  const textPositionStyles = {
    "bottom-left": {
      position: "absolute",
      bottom: { xs: theme.spacing(22), md: theme.spacing(6) },
      left: theme.spacing(6),
      right: theme.spacing(6),
      zIndex: 2,
    },
    center: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      textAlign: "center",
      zIndex: 2,
      width: "90%",
      maxWidth: "800px",
    },
  };

  const heroSx = {
    position: "relative",
    width: {
      xs: `calc(100% + ${theme.spacing(4)})`,
      sm: `calc(100% + ${theme.spacing(6)})`,
      md: "100%",
    },
    ml: { xs: -2, sm: -3, md: 0 },
    mr: { xs: -2, sm: -3, md: 0 },
    height: { xs: "100vh", md: "auto" },
    minHeight: { xs: "100vh", md: "360px" },
    maxHeight: { xs: "none", md: "720px" },
    aspectRatio: { xs: "auto", md: "16 / 7" },
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[12],
    background: "transparent",
    ...sx,
  };

  const inferVideoType = (src) => {
    if (!src) return "video/mp4";
    const lower = src.toLowerCase();
    if (lower.endsWith(".mov")) return "video/quicktime";
    if (lower.endsWith(".webm")) return "video/webm";
    if (lower.endsWith(".ogg") || lower.endsWith(".ogv")) return "video/ogg";
    return "video/mp4";
  };

  return (
    <Card
      component={motion.div}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      elevation={8}
      sx={heroSx}
      {...props}
    >
      {/* Media Background */}
      {mediaType === "video" ? (
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
           preload="metadata"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
        >
          <source src={mediaSrc} type={inferVideoType(mediaSrc)} />
          Your browser does not support the video tag.
        </Box>
      ) : (
        <Box
          component="picture"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
          }}
        >
          {mediaSrcSet && <source srcSet={mediaSrcSet} sizes={mediaSizes} />}
          <Box
            component="img"
            src={mediaSrc}
            alt={mediaAlt}
            loading={mediaLoading}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      )}

      {/* Dark Overlay */}
      {darkOverlay && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, " + overlayOpacity + ")",
            zIndex: 1,
          }}
        />
      )}

      {/* Logo */}
      {showLogo && logoSrc && (
        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{
            position: "absolute",
            top: resolvedLogoTop,
            left: resolvedLogoLeft,
            transform: typeof logoTop === "string" && logoTop.includes("%") ? "translate(-50%, -50%)" : "none",
            zIndex: 3,
          }}
        >
          <Box
            component="img"
            src={logoSrc}
            alt="Logo"
            sx={{
              height: resolvedLogoHeight,
              maxHeight: { xs: "32vh", md: "260px" },
              width: "auto",
              maxWidth: { xs: "70vw", md: "320px" },
              objectFit: "contain",
            }}
          />
        </Box>
      )}

      {/* Text Content */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{
          ...textPositionStyles[textPosition],
          color: theme.palette.common.white,
        }}
      >
        {subtitle && (
          <Typography
            component={motion.h1}
            variants={itemVariants}
            variant="h2"
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.fontWeightBold,
              fontSize: {
                xs: theme.typography.pxToRem(24),
                sm: theme.typography.pxToRem(28),
                md: theme.typography.pxToRem(32),
                lg: theme.typography.pxToRem(36),
              },
              marginBottom: theme.spacing(2),
              textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
              lineHeight: 1.2,
            }}
          >
            {subtitle}
          </Typography>
        )}

        {description && (
          <Typography
            component={motion.p}
            variants={itemVariants}
            variant="h5"
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.fontWeightRegular,
              fontSize: {
                xs: theme.typography.pxToRem(18),
                sm: theme.typography.pxToRem(20),
                md: theme.typography.pxToRem(22),
                lg: theme.typography.pxToRem(24),
              },
              lineHeight: 1.5,
              maxWidth: "900px",
              textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
              [theme.breakpoints.down("sm")]: {
                fontSize: theme.typography.pxToRem(16),
              },
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {/* Mobile scroll cue */}
      <Box
        sx={{
          position: "absolute",
          bottom: theme.spacing(6),
          left: "50%",
          transform: "translateX(-50%)",
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          gap: theme.spacing(1),
          color: "rgba(255,255,255,0.85)",
          zIndex: 3,
          pointerEvents: "none",
          letterSpacing: "0.08em",
          fontSize: theme.typography.pxToRem(12),
          textTransform: "uppercase",
          fontFamily: theme.typography.fontFamily,
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 42,
            border: "1px solid rgba(255,255,255,0.6)",
            borderRadius: "999px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&::after": {
              content: '""',
              width: 8,
              height: 8,
              borderLeft: "2px solid rgba(255,255,255,0.85)",
              borderBottom: "2px solid rgba(255,255,255,0.85)",
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%) rotate(-45deg)",
            },
          }}
        />
        Scroll
      </Box>
    </Card>
  );
};

HeroSection.propTypes = {
  mediaType: PropTypes.oneOf(["image", "video"]),
  mediaSrc: PropTypes.string.isRequired,
  mediaSrcSet: PropTypes.string,
  mediaSizes: PropTypes.string,
  mediaAlt: PropTypes.string,
  mediaLoading: PropTypes.oneOf(["lazy", "eager"]),
  logoSrc: PropTypes.string,
  logoHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  logoTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  logoLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  showLogo: PropTypes.bool,
  darkOverlay: PropTypes.bool,
  overlayOpacity: PropTypes.number,
  textPosition: PropTypes.oneOf(["bottom-left", "center"]),
  sx: PropTypes.object,
};

export default HeroSection;
